<app-main-layout>
  <div class="px-6 py-6 bg-gray-50 min-h-screen">
    <!-- Header -->
    <div class="flex flex-row justify-between items-end mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Validate Orders</h1>
        <p class="text-sm text-gray-500 mt-1">
          Confirm what's being packed matches what was ordered before it ships.
        </p>
      </div>
      <div class="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
        <span class="h-2 w-2 rounded-full bg-amber-400"></span>
        <span class="text-sm font-semibold text-gray-700">{{ queueCount }}</span>
        <span class="text-sm text-gray-400">{{ queueCountLabel }}</span>
      </div>
    </div>

    <!-- Filters -->
    @if (activeTab !== 'RECEIPTS') {
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 mb-6">
        <div class="flex flex-row items-end flex-wrap gap-3">
          <div class="relative flex-1 min-w-[220px]">
            <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Product</label>
            <!-- Picking appends to the comma-separated list and applies it;
            Enter without picking applies whatever was typed. -->
            <app-product-picker
              placeholder="Name, SKU or barcode - comma-separate for multiple"
              panelWidth="min(520px, 92vw)"
              noMatchHint="Press Enter to filter by the text as typed"
              [products]="products"
              [commaSeparated]="true"
              [showAllOnEmpty]="false"
              [autoActiveFirst]="false"
              [(query)]="filterSearch"
              (focused)="onProductSearchFocus()"
              (picked)="applyFilters()"
              (enterKey)="applyFilters()"
              />
          </div>
          <div class="w-44">
            <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Order No.</label>
            <input
              type="text"
              class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all text-sm"
              [(ngModel)]="filterOrderNo"
              (keyup.enter)="applyFilters()"
              placeholder="e.g. 10234, 10240"
              />
          </div>
          <div class="w-44">
            <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Postcode</label>
            <input
              type="text"
              class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all text-sm"
              [(ngModel)]="filterPostCode"
              (keyup.enter)="applyFilters()"
              placeholder="e.g. CO6 1EP, CO1 2AB"
              />
          </div>
          <div class="w-44">
            <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Method</label>
            <button
              class="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-white text-sm text-left transition-all"
              [ngClass]="filterDeliveryMethods.length ? 'text-gray-800 font-medium' : 'text-gray-500'"
              [matMenuTriggerFor]="methodMenu"
              >
              <span class="truncate">{{ methodFilterLabel }}</span>
              <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400 flex-shrink-0">expand_more</mat-icon>
            </button>
            <mat-menu #methodMenu="matMenu">
              <div class="py-1 min-w-[180px]">
                @for (m of deliveryMethodOptions; track m) {
                  <label
                    class="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                    (click)="$event.stopPropagation()"
                    >
                    <input
                      type="checkbox"
                      class="rounded border-gray-300"
                      [checked]="filterDeliveryMethods.includes(m.value)"
                      (change)="toggleMethodFilter(m.value)"
                      />
                    {{ m.label }}
                  </label>
                }
              </div>
            </mat-menu>
          </div>
          <div class="w-48">
            <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Dispatch Group</label>
            <button
              class="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-white text-sm text-left transition-all"
              [ngClass]="filterDispatchGroups.length ? 'text-gray-800 font-medium' : 'text-gray-500'"
              [matMenuTriggerFor]="groupFilterMenu"
              >
              <span class="truncate">{{ dispatchGroupFilterLabel }}</span>
              <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400 flex-shrink-0">expand_more</mat-icon>
            </button>
            <mat-menu #groupFilterMenu="matMenu">
              <div class="py-1 min-w-[200px]">
                @for (g of dispatchGroupFilterOptions; track g) {
                  <label
                    class="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                    (click)="$event.stopPropagation()"
                    >
                    <input
                      type="checkbox"
                      class="rounded border-gray-300"
                      [checked]="filterDispatchGroups.includes(g.value)"
                      (change)="toggleDispatchGroupFilter(g.value)"
                      />
                    {{ g.label }}
                  </label>
                }
              </div>
            </mat-menu>
          </div>
          <button
            class="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-semibold text-sm shadow-sm hover:shadow-md hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all"
            (click)="applyFilters()"
            >
            Filter
          </button>
          @if (hasActiveFilters) {
            <button
              class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-gray-500 hover:text-gray-800 hover:bg-gray-50 font-semibold text-sm transition-all"
              (click)="clearFilters()"
              >
              <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
              Clear
            </button>
          }
        </div>
        <!-- Order date range -->
        <div class="mt-4 pt-4 border-t border-gray-100 flex flex-col lg:flex-row lg:items-center gap-3">
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-white text-xs font-semibold uppercase tracking-wide text-gray-700 transition-colors"
            [matMenuTriggerFor]="dateFieldMenu"
            matTooltip="Choose which date the range applies to"
            >
            <mat-icon class="!text-[16px] !h-4 !w-4 !text-amber-500">{{ dateFieldIcon }}</mat-icon>
            {{ dateFieldLabel }}
            <mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">expand_more</mat-icon>
          </button>
          <mat-menu #dateFieldMenu="matMenu">
            @for (f of dateFields; track f) {
              <button mat-menu-item (click)="setDateField(f.key)">
                <mat-icon>{{ f.icon }}</mat-icon>
                <span class="flex flex-col leading-tight">
                  <span [class.font-semibold]="dateField === f.key">{{ f.label }}</span>
                  <span class="text-[11px] text-gray-400">{{ f.hint }}</span>
                </span>
              </button>
            }
          </mat-menu>
          <div class="inline-flex flex-wrap rounded-lg border border-gray-200 bg-gray-50 p-1 gap-1 w-fit">
            @for (preset of datePresets; track preset) {
              <button
                type="button"
                class="px-3 py-1.5 text-xs font-semibold uppercase rounded-md transition-colors"
                [ngClass]="datePreset === preset.key ? 'bg-neutral-800 text-white' : 'text-gray-600 hover:bg-gray-200'"
                (click)="applyDatePreset(preset.key)"
                >
                {{ preset.label }}
              </button>
            }
          </div>
          <div class="flex flex-row flex-wrap items-center gap-2 text-sm">
            <label class="text-xs font-medium text-gray-400 uppercase tracking-wide">From</label>
            <input
              type="date"
              class="p-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-w-0"
              [(ngModel)]="filterDtFrom"
              (change)="onCustomDateChange()"
              />
            <label class="text-xs font-medium text-gray-400 uppercase tracking-wide">To</label>
            <input
              type="date"
              class="p-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-w-0"
              [(ngModel)]="filterDtTo"
              (change)="onCustomDateChange()"
              />
          </div>
        </div>
      </div>
    }

    <!-- Tabs -->
    <div class="flex items-center gap-1 mb-4 overflow-x-auto">
      @for (t of tabs; track t) {
        <button
          class="flex flex-shrink-0 items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
        [ngClass]="
          t.value === 'RECEIPTS'
            ? (activeTab === t.value
                ? 'bg-fuchsia-700 text-white shadow-lg shadow-fuchsia-300 ring-2 ring-fuchsia-300 ml-3'
                : 'bg-fuchsia-600 text-white hover:bg-fuchsia-700 shadow-md shadow-fuchsia-200 ml-3')
            : activeTab === t.value
              ? 'bg-neutral-900 text-amber-400'
              : 'text-gray-500 hover:bg-white hover:text-gray-800'
        "
          (click)="switchTab(t.value)"
          >
          <mat-icon class="!text-[16px] !h-4 !w-4">{{ t.icon }}</mat-icon>
          {{ t.label }}
          @if (tabCounts[t.value] !== undefined) {
            <span
              class="min-w-[22px] text-center text-[11px] font-bold px-1.5 py-0.5 rounded-full"
          [ngClass]="
            t.value === 'RECEIPTS'
              ? 'bg-white text-fuchsia-700'
              : activeTab === t.value
                ? 'bg-amber-400 text-neutral-900'
                : 'bg-gray-200 text-gray-600'
          "
              >
              {{ tabCounts[t.value] }}
            </span>
          }
        </button>
      }
    </div>

    <!-- Incoming stocks -->
    @if (activeTab === 'RECEIPTS') {
      <app-purchase-receipts (changed)="onReceiptsChanged()"></app-purchase-receipts>
    }

    <!-- Queue -->
    @if (activeTab !== 'RECEIPTS') {
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        @if (!selectedOrderIds.size) {
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 px-5 py-4 border-b border-gray-100">
            <div class="flex items-baseline gap-3">
              <span class="font-semibold text-gray-800">{{ queueTitle }}</span>
              @if (rangeLabel) {
                <span class="text-xs text-gray-400">{{ rangeLabel }}</span>
              }
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <div class="flex items-center gap-1.5 text-xs text-gray-500">
                <span>Select first</span>
                <input
                  type="number"
                  min="1"
                  [max]="queueCount"
                  class="w-16 px-2 py-1 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-amber-400"
                  [(ngModel)]="selectFirstCount"
                  (keyup.enter)="selectFirst()"
                  placeholder="25"
                  />
                <button class="font-semibold underline text-gray-700 hover:text-gray-900 disabled:opacity-50" [disabled]="selectingAll" (click)="selectFirst()">Select</button>
              </div>
              <button
                mat-raised-button
                color="accent"
                (click)="exportToExcel()"
                [disabled]="exporting || !queueCount"
                matTooltip="Excel pick sheet for the current tab and filters"
                >
                <mat-icon>download</mat-icon> {{ exporting ? 'Preparing...' : 'Download Pick Sheet' }}
              </button>
              <button
                class="flex items-center justify-center h-8 w-8 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-700"
                (click)="loadQueue()"
                matTooltip="Refresh"
                >
                <mat-icon class="!text-[20px]">refresh</mat-icon>
              </button>
            </div>
          </div>
        }
        <!-- Bulk actions bar - replaces the header row once something's selected -->
        @if (selectedOrderIds.size) {
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 px-5 py-3 border-b border-amber-100 bg-amber-50">
            <div class="flex flex-wrap items-center gap-3">
              <span class="text-sm font-semibold text-amber-800">
                {{ selectedOrderIds.size }} order{{ selectedOrderIds.size === 1 ? '' : 's' }} selected
              </span>
              @if (canSelectAllMatching) {
                <button
                  class="text-xs font-semibold text-amber-700 hover:text-amber-900 underline disabled:opacity-50"
                  [disabled]="selectingAll"
                  (click)="selectAllMatching()"
                  >
                  {{ selectingAll ? 'Selecting...' : 'Select all ' + queueCount + ' orders' }}
                </button>
              }
              @if (allMatchingSelected) {
                <span class="text-xs text-amber-700">
                  {{ selectedOrderIds.size === queueCount ? 'across all pages' : 'across pages' }}
                </span>
              }
              <div class="flex items-center gap-1.5 text-xs text-amber-800">
                <span>Select first</span>
                <input
                  type="number"
                  min="1"
                  [max]="queueCount"
                  class="w-16 px-2 py-1 rounded-lg border border-amber-200 bg-white text-sm text-gray-800 outline-none focus:border-amber-400"
                  [(ngModel)]="selectFirstCount"
                  (keyup.enter)="selectFirst()"
                  placeholder="25"
                  />
                <button class="font-semibold underline text-amber-700 hover:text-amber-900 disabled:opacity-50" [disabled]="selectingAll" (click)="selectFirst()">Select</button>
              </div>
              <button class="text-xs font-semibold text-amber-700 hover:text-amber-900 underline" (click)="clearSelection()">
                Clear
              </button>
            </div>
            <button
              class="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-neutral-900 text-amber-400 font-semibold text-xs shadow-sm hover:bg-neutral-800 transition-all disabled:opacity-50"
              [matMenuTriggerFor]="bulkGroupMenu"
              [disabled]="bulkAssigning"
              >
              @if (bulkAssigning) {
                <mat-spinner diameter="14" class="!text-amber-400"></mat-spinner>
              }
              @if (!bulkAssigning) {
                <mat-icon class="!text-[16px] !h-4 !w-4">label</mat-icon>
              }
              Set Dispatch Group
              <mat-icon class="!text-[16px] !h-4 !w-4">expand_more</mat-icon>
            </button>
            <mat-menu #bulkGroupMenu="matMenu">
              @for (g of dispatchGroupOptions; track g) {
                <button mat-menu-item (click)="bulkAssignDispatchGroup(g.value)">
                  <mat-icon>{{ g.icon }}</mat-icon>
                  <span>{{ g.label }}</span>
                </button>
              }
            </mat-menu>
          </div>
        }
        <!-- min-height roughly matches a full page of rows, so swapping in
        the loaded table (or the empty state) doesn't yank the page -
        this was the main source of layout shift on this screen. -->
        @if (queueLoading) {
          <div class="flex justify-center items-center min-h-[520px]">
            <mat-spinner diameter="32"></mat-spinner>
          </div>
        }
        @if (!queueLoading && !processingOrders.length) {
          <div class="flex flex-col items-center justify-center min-h-[520px] text-gray-400">
            <mat-icon class="!text-4xl !h-10 !w-10 mb-2 opacity-40">inventory_2</mat-icon>
            <div>{{ hasActiveFilters ? 'No orders match those filters.' : 'No orders currently processing.' }}</div>
          </div>
        }
        @if (!queueLoading && processingOrders.length) {
          <div class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50">
                  <th class="pl-5 pr-2 py-3 w-8">
                    <input
                      type="checkbox"
                      class="rounded border-gray-300 cursor-pointer"
                      [checked]="allSelectedOnPage"
                      (change)="toggleSelectAll()"
                      />
                  </th>
                  <th class="px-5 py-3">Order</th>
                  <th class="px-4 py-3">Customer</th>
                  <th class="px-4 py-3">Delivery</th>
                  <th class="px-4 py-3">Timeline</th>
                  <th class="px-4 py-3">Validated</th>
                  <th class="px-4 py-3">Dispatch Group</th>
                  <th class="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                @for (o of processingOrders; track o) {
                  <tr
                    class="group border-b border-gray-50 last:border-b-0 even:bg-gray-50/30 hover:bg-amber-50/40 cursor-pointer transition-colors align-top"
                    [ngClass]="{ '!bg-amber-50/60': isSelected(o) }"
                    (click)="openOrder(o)"
                    >
                    <td class="pl-5 pr-2 py-4" (click)="$event.stopPropagation()">
                      <input
                        type="checkbox"
                        class="rounded border-gray-300 cursor-pointer mt-1"
                        [checked]="isSelected(o)"
                        (change)="toggleSelect(o)"
                        />
                    </td>
                    <!-- Order -->
                    <td class="px-5 py-4 whitespace-nowrap">
                      <div class="flex items-start gap-3">
                        <div class="flex items-center justify-center h-9 w-9 rounded-xl bg-neutral-900 text-amber-400 font-bold text-[11px] flex-shrink-0">
                          #{{ o.orderNo?.toString()?.slice(-4) }}
                        </div>
                        <div>
                          <div class="font-semibold text-gray-900">#{{ o.orderNo }}</div>
                          <div class="text-xs text-gray-400">{{ o.orderDate | date: 'dd MMM yyyy, HH:mm' }}</div>
                          <span class="inline-block mt-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {{ o.OrderItems?.length || 0 }} item{{ (o.OrderItems?.length || 0) === 1 ? '' : 's' }}
                          </span>
                        </div>
                      </div>
                    </td>
                    <!-- Customer -->
                    <td class="px-4 py-4 max-w-[170px]">
                      <div class="text-gray-800 font-medium truncate">{{ customerName(o) }}</div>
                      @if (shippingPostCode(o) !== '-') {
                        <a
                          class="inline-flex items-center gap-1 text-xs text-gray-500 mt-1 px-1.5 py-0.5 -ml-1.5 rounded-md hover:bg-amber-100 hover:text-gray-900 transition-colors"
                          [href]="mapsUrl(o)"
                          target="_blank"
                          rel="noopener"
                          (click)="$event.stopPropagation()"
                          matTooltip="Open delivery address in Google Maps"
                          >
                          <mat-icon class="!text-[13px] !h-[13px] !w-[13px] !text-amber-500">place</mat-icon>
                          {{ shippingPostCode(o) }}
                          <mat-icon class="!text-[12px] !h-3 !w-3 opacity-60">open_in_new</mat-icon>
                        </a>
                      }
                    </td>
                    <!-- Delivery: method + terms -->
                    <td class="px-4 py-4 whitespace-nowrap">
                      <div class="flex flex-col gap-1.5 items-start">
                        @if (o.deliveryMethod) {
                          <span
                            class="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full w-fit"
                            [ngClass]="o.deliveryMethod === 'COLLECTION' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'"
                            >
                            <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">{{ deliveryMethodIcon(o) }}</mat-icon>
                            {{ deliveryMethodLabel(o) }}
                          </span>
                        }
                        @if (deliveryTermsShort(o); as terms) {
                          <span
                            class="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 w-fit"
                            [matTooltip]="o.deliveryTerms"
                            >
                            <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">schedule</mat-icon>
                            {{ terms }}{{ terms === 'NaN' ? '' : ' days' }}
                          </span>
                        }
                        @if (!o.deliveryMethod && !deliveryTermsShort(o)) {
                          <span class="text-gray-300 text-xs">-</span>
                        }
                      </div>
                    </td>
                    <!-- Timeline: paid -> dispatch by -->
                    <td class="px-4 py-4 whitespace-nowrap">
                      <div class="flex flex-col gap-1.5">
                        @if (o.paymentDate) {
                          <div class="flex items-center gap-1.5 text-xs">
                            <mat-icon class="!text-[14px] !h-[14px] !w-[14px] !text-gray-400">payments</mat-icon>
                            <span class="text-gray-600 font-medium">{{ o.paymentDate | date: 'dd MMM yyyy, HH:mm' }}</span>
                          </div>
                        }
                        @if (dispatchDate(o); as dDate) {
                          <div
                            class="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full w-fit"
                            [ngClass]="dispatchUrgencyClass(o)"
                            matTooltip="Dispatch by (paid date + delivery terms)"
                            >
                            <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">event</mat-icon>
                            {{ dDate | date: 'dd MMM yyyy' }}
                          </div>
                        }
                        @if (!o.paymentDate && !dispatchDate(o)) {
                          <span class="text-gray-300 text-xs">-</span>
                        }
                      </div>
                    </td>
                    <!-- Validated -->
                    <td class="px-4 py-4 whitespace-nowrap">
                      @if (o.lastValidatedAt) {
                        <div>
                          <span
                            class="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-700 w-fit"
                            [matTooltip]="o.lastValidatedBy ? ('Validated by ' + o.lastValidatedBy) : 'Last item checked off on this order'"
                            >
                            <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">check_circle</mat-icon>
                            {{ o.lastValidatedAt | date: 'dd MMM yyyy, HH:mm' }}
                          </span>
                          @if (o.lastValidatedBy) {
                            <div class="mt-1 max-w-[150px]">
                              @if (o.lastValidatedByName && o.lastValidatedByName !== o.lastValidatedBy) {
                                <div class="text-[11px] font-medium text-gray-600 truncate">
                                  {{ o.lastValidatedByName }}
                                </div>
                              }
                              <div class="text-[11px] text-gray-400 truncate">{{ o.lastValidatedBy }}</div>
                            </div>
                          }
                        </div>
                      } @else {
                        <span class="text-gray-300 text-xs">-</span>
                      }
                      <!-- What has happened with this order's delivery lines -->
                      @if (deliveryStatus(o); as ds) {
                        <div class="mt-2 flex flex-col items-start gap-1">
                          <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-normal" [ngClass]="ds.tone">
                            {{ ds.headline }}
                          </span>
                          @if (ds.tags.length) {
                            <div class="flex flex-wrap gap-1">
                              @for (tag of ds.tags; track tag) {
                                <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" [ngClass]="tag.cls">
                                  {{ tag.label }}
                                </span>
                              }
                            </div>
                          }
                        </div>
                      }
                    </td>
                    <!-- Dispatch group -->
                    <td class="px-4 py-4 whitespace-nowrap">
                      <button
                        class="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
                        [ngClass]="dispatchGroupOptionFor(o) ? dispatchGroupOptionFor(o)!.badgeClass + ' border-transparent' : 'border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600'"
                        [matMenuTriggerFor]="groupMenu"
                        (click)="$event.stopPropagation()"
                        >
                        <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">{{ dispatchGroupOptionFor(o)?.icon || 'label' }}</mat-icon>
                        {{ dispatchGroupOptionFor(o)?.label || 'Set Group' }}
                      </button>
                      <mat-menu #groupMenu="matMenu">
                        @for (g of dispatchGroupOptions; track g) {
                          <button mat-menu-item (click)="assignDispatchGroup(o, g.value)">
                            <mat-icon>{{ g.icon }}</mat-icon>
                            <span>{{ g.label }}</span>
                          </button>
                        }
                      </mat-menu>
                    </td>
                    <!-- Action -->
                    <td class="px-5 py-4 whitespace-nowrap">
                      <button
                        class="flex items-center gap-1 px-4 py-2 rounded-xl border border-amber-300 text-amber-600 font-semibold text-xs group-hover:bg-amber-400 group-hover:text-neutral-900 group-hover:border-amber-400 transition-all"
                        >
                        {{ activeTab === 'PROCESSING' ? 'Validate' : 'View' }}
                        <mat-icon class="!text-[16px] !h-4 !w-4">{{ activeTab === 'PROCESSING' ? 'arrow_forward' : 'visibility' }}</mat-icon>
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
        @if (!queueLoading && processingOrders.length) {
          <mat-paginator
            [pageSizeOptions]="[10, 20]"
            showFirstLastButtons
            [length]="queueCount"
            [pageSize]="queueItemsPerPage"
            [pageIndex]="queuePageNumber"
            (page)="updateQueueList($event)"
            >
          </mat-paginator>
        }
      </div>
    }
  </div>
</app-main-layout>
